import { z } from "zod";

import type { DevtoolsBoundingBox } from "@/components/base/map/MapBoxComponent.types";
import { ServerMvtLayerId } from "@/features/map/lib/build-server-mvt-layers";

const ZOOM_MIN = 0;
const ZOOM_MAX = 22;

const LAT_RANGE = { min: -90, max: 90 };
const LNG_RANGE = { min: -180, max: 180 };

/** Known org-overview MVT layer IDs for URL layer visibility. */
export const ORG_MVT_LAYER_IDS = [
	ServerMvtLayerId.Jobs,
	ServerMvtLayerId.Tickets,
	ServerMvtLayerId.Assets,
	ServerMvtLayerId.Records,
] as const;

function optionalNumericParam(
	min: number,
	max: number,
): z.ZodType<number | undefined> {
	return z
		.union([z.string(), z.number()])
		.optional()
		.transform((v) => (v === undefined || v === "" ? undefined : Number(v)))
		.pipe(z.number().min(min).max(max).optional());
}

/**
 * Shared camera / basemap / layers search params used by Org and Destiny maps.
 */
export const mapViewSearchSchema = z.object({
	lat: optionalNumericParam(-90, 90),
	lng: optionalNumericParam(-180, 180),
	z: optionalNumericParam(ZOOM_MIN, ZOOM_MAX),
	bearing: optionalNumericParam(-180, 180),
	pitch: optionalNumericParam(0, 85),
	bbox: z.string().optional(),
	layers: z.string().optional(),
	basemap: z.enum(["road", "satellite", "hybrid"]).optional(),
});

export type MapViewSearch = z.infer<typeof mapViewSearchSchema>;

/** Org overview map search — view params plus sub-organization filter. */
export const orgMapSearchSchema = mapViewSearchSchema.extend({
	subOrg: z.string().optional(),
});

export type OrgMapSearch = z.infer<typeof orgMapSearchSchema>;

/**
 * Parse comma-separated `subOrg` search param into id list.
 * Empty / missing → org-level ("All") selection.
 */
export function subOrgIdsFromSearchParam(subOrg: string | undefined): string[] {
	if (!subOrg || subOrg.trim() === "") return [];
	return subOrg
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
}

function isInRange(
	value: number,
	range: { min: number; max: number },
): boolean {
	return (
		typeof value === "number" &&
		!Number.isNaN(value) &&
		value >= range.min &&
		value <= range.max
	);
}

/**
 * Parse bbox string `minLng,minLat,maxLng,maxLat` into a bounding box, or null.
 */
export function parseBbox(
	bbox: string | undefined,
): DevtoolsBoundingBox | null {
	if (!bbox || typeof bbox !== "string") return null;
	const parts = bbox.split(",").map((p) => Number(p.trim()));
	if (parts.length !== 4) return null;
	const [minLng, minLat, maxLng, maxLat] = parts;
	if (
		![minLng, minLat, maxLng, maxLat].every(Number.isFinite) ||
		!isInRange(minLat, LAT_RANGE) ||
		!isInRange(maxLat, LAT_RANGE) ||
		!isInRange(minLng, LNG_RANGE) ||
		!isInRange(maxLng, LNG_RANGE) ||
		minLat >= maxLat ||
		minLng >= maxLng
	) {
		return null;
	}
	return {
		minLatitude: minLat,
		maxLatitude: maxLat,
		minLongitude: minLng,
		maxLongitude: maxLng,
	};
}

/**
 * Serialize a bounding box to the URL `bbox` param value.
 */
export function formatBbox(bbox: DevtoolsBoundingBox): string {
	return `${bbox.minLongitude},${bbox.minLatitude},${bbox.maxLongitude},${bbox.maxLatitude}`;
}

/**
 * Build layer visibility from a URL `layers` param.
 *
 * - Missing / undefined → all known ids visible
 * - Explicit `""` → all known ids off
 * - Comma-separated ids → those ids on, others off
 */
export function layerVisibilityFromSearch(
	layersParam: string | undefined,
	knownIds: readonly string[],
): Record<string, boolean> {
	if (layersParam === "") {
		return Object.fromEntries(knownIds.map((id) => [id, false]));
	}

	const visible = new Set(
		layersParam
			? layersParam
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			: [],
	);

	if (visible.size === 0) {
		return Object.fromEntries(knownIds.map((id) => [id, true]));
	}

	return Object.fromEntries(knownIds.map((id) => [id, visible.has(id)]));
}

/**
 * Org overview layer visibility — includes legacy bookmark handling for
 * URLs that listed jobs/tickets/assets before records existed.
 */
export function orgLayerVisibilityFromSearch(
	layersParam: string | undefined,
): Record<string, boolean> {
	const visibility = layerVisibilityFromSearch(layersParam, ORG_MVT_LAYER_IDS);

	if (layersParam === "" || layersParam == null || layersParam.length === 0) {
		return visibility;
	}

	const visible = new Set(
		layersParam
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean),
	);

	const legacyWithoutRecords =
		visible.has(ServerMvtLayerId.Jobs) &&
		visible.has(ServerMvtLayerId.Tickets) &&
		visible.has(ServerMvtLayerId.Assets) &&
		!visible.has(ServerMvtLayerId.Records) &&
		visible.size === 3;

	if (!legacyWithoutRecords) return visibility;

	return {
		...visibility,
		[ServerMvtLayerId.Records]: true,
	};
}

/**
 * Serialize layer visibility to a URL param: comma-separated visible ids.
 * Empty string when none visible; `undefined` when all known ids are visible.
 */
export function layerVisibilityToParam(
	visibility: Record<string, boolean>,
	knownIds: readonly string[],
): string | undefined {
	const visible = knownIds.filter((id) => visibility[id] !== false);
	if (visible.length === 0) return "";
	if (visible.length === knownIds.length) return undefined;
	return visible.join(",");
}

/** Fields written into the browser address bar for map view sync. */
export interface MapSearchHistoryFields {
	lng?: number;
	lat?: number;
	z?: number;
	bearing?: number;
	pitch?: number;
	bbox?: string;
	subOrg?: string;
	layers?: string;
	basemap?: string;
}

const CAMERA_COORD_EPSILON = 1e-6;
const CAMERA_ZOOM_EPSILON = 1e-4;

function nearlyEqual(
	a: number | undefined,
	b: number | undefined,
	epsilon: number,
): boolean {
	const left = a ?? 0;
	const right = b ?? 0;
	return Math.abs(left - right) <= epsilon;
}

/**
 * True when two map search snapshots describe the same camera (and bbox).
 * Used to skip no-op `navigate` calls that otherwise nest router updates
 * (React error #185) when Mapbox reports micro-jitter at unstable cameras.
 */
export function isSameMapViewCameraSearch(
	a: Pick<MapViewSearch, "lat" | "lng" | "z" | "bearing" | "pitch" | "bbox">,
	b: Pick<MapViewSearch, "lat" | "lng" | "z" | "bearing" | "pitch" | "bbox">,
): boolean {
	return (
		nearlyEqual(a.lat, b.lat, CAMERA_COORD_EPSILON) &&
		nearlyEqual(a.lng, b.lng, CAMERA_COORD_EPSILON) &&
		nearlyEqual(a.z, b.z, CAMERA_ZOOM_EPSILON) &&
		nearlyEqual(a.bearing, b.bearing, CAMERA_COORD_EPSILON) &&
		nearlyEqual(a.pitch, b.pitch, CAMERA_COORD_EPSILON) &&
		(a.bbox ?? "") === (b.bbox ?? "")
	);
}

/**
 * Ensure optional map search params appear in the address bar via
 * `history.replaceState` (TanStack Router may omit optional search keys).
 */
export function replaceMapSearchInHistory(
	search: MapSearchHistoryFields,
): void {
	if (typeof window === "undefined") return;

	const params = new URLSearchParams();
	if (search.lng != null) params.set("lng", String(search.lng));
	if (search.lat != null) params.set("lat", String(search.lat));
	if (search.z != null) params.set("z", String(search.z));
	if (search.bearing != null && search.bearing !== 0) {
		params.set("bearing", String(search.bearing));
	}
	if (search.pitch != null && search.pitch !== 0) {
		params.set("pitch", String(search.pitch));
	}
	if (search.bbox) params.set("bbox", search.bbox);
	if (search.subOrg) params.set("subOrg", search.subOrg);
	if (search.layers !== undefined) params.set("layers", search.layers);
	if (search.basemap) params.set("basemap", search.basemap);

	const query = params.toString();
	const newSearch = query ? `?${query}` : "";
	if (window.location.search !== newSearch) {
		window.history.replaceState(
			window.history.state,
			"",
			window.location.pathname + newSearch,
		);
	}
}
